import { useCallback, useEffect, useState } from 'react';
import { changesApi } from '../../../services/changesApi';
import type { ChangeCatalogItem } from '../../../types/changes';

interface Options {
  /** Só cadastros ativos — usado nos selects do formulário. */
  onlyActive?: boolean;
  enabled?: boolean;
}

/**
 * Cadastros dos selects do formulário de mudança.
 *
 * As listas são independentes e todas necessárias para montar o formulário,
 * então vão juntas em `allSettled`: se uma falhar, as outras ainda preenchem
 * e a tela continua utilizável.
 */
export function useChangeCatalogs(options: Options = {}) {
  const onlyActive = options.onlyActive ?? true;
  const enabled = options.enabled ?? true;
  const [categories, setCategories] = useState<ChangeCatalogItem[]>([]);
  const [requesterAreas, setRequesterAreas] = useState<ChangeCatalogItem[]>([]);
  const [taskTypes, setTaskTypes] = useState<ChangeCatalogItem[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [categoriesResult, areasResult, typesResult] = await Promise.allSettled([
      changesApi.listCatalog('categories', { only_active: onlyActive }),
      changesApi.listCatalog('requester-areas', { only_active: onlyActive }),
      changesApi.listCatalog('task-types', { only_active: onlyActive }),
    ]);
    setCategories(categoriesResult.status === 'fulfilled' ? categoriesResult.value.items : []);
    setRequesterAreas(areasResult.status === 'fulfilled' ? areasResult.value.items : []);
    setTaskTypes(typesResult.status === 'fulfilled' ? typesResult.value.items : []);
    setLoading(false);
  }, [onlyActive]);

  useEffect(() => {
    if (!enabled) return;
    void load();
  }, [enabled, load]);

  return { categories, requesterAreas, taskTypes, loading, reload: load };
}
